import { toOption } from './formOptions';

const SCALAR_TYPES = ['text', 'email', 'number', 'phone', 'date', 'select'];
const LIST_TYPES = ['checkbox', 'multiselect'];

const USER_FIELDS = {
  firstName: { types: SCALAR_TYPES, get: (user) => user.firstName },
  lastName: { types: SCALAR_TYPES, get: (user) => user.lastName },
  email: { types: SCALAR_TYPES, get: (user) => user.email },
  phone: { types: SCALAR_TYPES, get: (user) => user.phone },
  street: { types: SCALAR_TYPES, get: (user) => user.address?.street },
  city: { types: SCALAR_TYPES, get: (user) => user.address?.city },
  state: { types: SCALAR_TYPES, get: (user) => user.address?.state },
  zipCode: { types: SCALAR_TYPES, get: (user) => user.address?.zipCode },
  dateOfBirth: {
    types: SCALAR_TYPES,
    pastDates: true,
    get: (user) => user.dateOfBirth,
  },
  anniversary: {
    types: SCALAR_TYPES,
    pastDates: true,
    get: (user) => user.anniversary,
  },

  interestTopics: {
    types: LIST_TYPES,
    editable: true,
    get: (user) =>
      user.interestTopics?.length ? user.interestTopics : undefined,
  },

  favoriteVenues: {
    types: LIST_TYPES,
    editable: true,
    get: (user, options) => {
      const ids = (user.favorites?.venues || []).map(String);
      return options
        .filter((option) => ids.includes(option.id))
        .map((option) => option.value);
    },
  },
};

export function getUserFieldKey({ userField, name, type }) {
  const key = USER_FIELDS[userField] ? userField : name;
  return USER_FIELDS[key]?.types.includes(type) ? key : undefined;
}

export function isPastDateField(field) {
  if (typeof field.pastDatesOnly === 'boolean') return field.pastDatesOnly;
  const key = getUserFieldKey(field);
  return !!key && !!USER_FIELDS[key].pastDates;
}

export function getUserFieldValue(user, field, options) {
  const key = getUserFieldKey(field);
  if (!user || !key) return undefined;

  const items = options && options.map(toOption);
  const value = USER_FIELDS[key].get(user, items || []);
  if (!Array.isArray(value)) return value || undefined;

  if (!value.length) return undefined;
  if (!items) return value;

  const values = items.map((item) => item.value);
  const shown = value.filter((item) => values.includes(item));
  return shown.length ? shown : undefined;
}

export function isLockedUserField(field) {
  const key = getUserFieldKey(field);
  return !!key && !USER_FIELDS[key].editable;
}

export function getAccountPrefill(user, fields, getOptions = () => undefined) {
  const prefill = { locked: {}, editable: {} };
  if (!user) return prefill;

  fields.forEach((field) => {
    const value = getUserFieldValue(user, field, getOptions(field));
    if (!value) return;
    const group = isLockedUserField(field) ? 'locked' : 'editable';
    prefill[group][field.name] = value;
  });
  return prefill;
}

export function getUserFields(fields, values) {
  return fields.reduce((acc, field) => {
    const key = getUserFieldKey(field);
    const value = values[field.name];
    if (key && value !== undefined && value !== '') acc[key] = value;
    return acc;
  }, {});
}

export function getUserFieldOptions(fields, getOptions = () => undefined) {
  return fields.reduce((acc, field) => {
    const key = getUserFieldKey(field);
    const options = getOptions(field);
    if (key && LIST_TYPES.includes(field.type) && options?.length) {
      acc[key] = options.map((option) => toOption(option).value);
    }
    return acc;
  }, {});
}
